import { capitalize, MediaType } from '@repo-level/core-front';
import { ButtonCross } from 'components/buttons';
import { ImageComponent } from 'components/Image';
import { Responsive } from 'components/internal/Responsive';
import { ModalSelect } from 'components/ModalSelect';
import { ScrollBlock } from 'components/ScrollBlock';
import { useTheme } from 'hooks/theme';
import { useComponentVisible } from 'hooks/visibility';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FieldStructureProps } from '../structures/FieldStructure';
import { FieldStructure } from '../structures/FieldStructure';
import type { ScOptionsProps, ScSelectMainProps } from './styled';
import {
  ScAnimatedPlaceholder,
  ScOptions,
  ScSelectBlock,
  ScSelectButton,
  ScSelectSearch,
} from './styled';

export interface SelectOption {
  icon?: string;
  name: React.ReactNode;
  nameS?: string;
  nameV?: string;
  value: string;
  label?: React.ReactNode;
  disabled?: boolean;
  image?: string;
}

type SelectButtonProps = FieldStructureProps &
  ScSelectMainProps & {
    selectedOption?: SelectOption;
    selected?: SelectOption['value'];
    defaultValue?: React.ReactNode;
    active?: boolean;
    handleOpen?: () => void;
    handleReset?: (e: any) => void;
    placeholder?: string;
    animatedPlaceholder?: string;
    canReset?: boolean;
    selectedOptionWithoutIcon?: boolean;
    noSelectioinIfOneOption?: boolean;
  };

export type SelectBlockProps = SelectButtonProps & {
  options: SelectOption[];
  onCustomOpen?: () => void;
  onSelect?: (value: SelectOption['value']) => void;
  onOpen?: () => void;
  onClose?: () => void;
  native?: boolean;
  search?: boolean;
  isFilter?: boolean;
  addons?: JSX.Element;
  forceDesktopView?: boolean;
} & Pick<OptionsBlockProps, 'minOptionHeight'>;

export const SelectBlock: (
  props: SelectBlockProps & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null = forwardRef(
  (
    {
      placeholder,
      animatedPlaceholder,
      options,
      onCustomOpen,
      selected,
      error,
      hideErrorMessage,
      onSelect,
      disabled,
      defaultValue,
      onOpen,
      onClose,
      native,
      search,
      canReset,
      withBorderMode,
      noSelectioinIfOneOption,
      addons,
      forceDesktopView,
      ...scProps
    }: SelectBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
      useComponentVisible(false);

    const handleOpen = () => {
      if (!!disabled || (noSelectioinIfOneOption && options.length < 2)) return;

      if (onCustomOpen) {
        onCustomOpen();
        return;
      }

      setIsComponentVisible(!isComponentVisible);
    };

    const selectedOption = useMemo(
      () =>
        typeof selected === 'undefined'
          ? undefined
          : options.find(({ value }) => value === selected),
      [selected, options]
    );

    const handleReset = useCallback(
      (e: any) => {
        e.stopPropagation();
        onSelect && onSelect('');
      },
      [onSelect]
    );

    const optionsBlock = (
      <OptionsBlock
        ref={ref}
        {...scProps}
        addons={addons}
        onChange={(value) => {
          onSelect && onSelect(value);
          handleOpen();
        }}
        options={options}
        search={search}
        selectedOption={selectedOption}
        withBorderMode={withBorderMode}
      />
    );

    return (
      <FieldStructure error={error} hideErrorMessage={hideErrorMessage}>
        <ScSelectBlock active={isComponentVisible} ref={forwardedRef}>
          <SelectButton
            ref={openerRef}
            {...scProps}
            active={isComponentVisible}
            animatedPlaceholder={animatedPlaceholder}
            canReset={canReset}
            disabled={disabled}
            error={error}
            handleOpen={handleOpen}
            handleReset={handleReset}
            noSelectioinIfOneOption={
              noSelectioinIfOneOption && options.length < 2
            }
            placeholder={placeholder}
            selected={selected}
            selectedOption={selectedOption}
          />
          {isComponentVisible && (
            <Responsive
              desktop={MediaType.DESKTOP_L}
              desktopL={optionsBlock}
              desktopXL={optionsBlock}
              mobile={MediaType.MOBILE_S}
              mobileS={
                forceDesktopView ? (
                  optionsBlock
                ) : (
                  <ModalSelect
                    borderRoundIconOption
                    fullHeight
                    onChange={(value) => {
                      onSelect && onSelect(value);
                      handleOpen();
                    }}
                    onClose={handleOpen}
                    options={options}
                    search={search}
                    selectedOption={selectedOption?.value}
                    title={placeholder || animatedPlaceholder || 'Select'}
                  />
                )
              }
              tablet={MediaType.DESKTOP_L}
            />
          )}
        </ScSelectBlock>
      </FieldStructure>
    );
  }
);

export const SelectButton = forwardRef<HTMLDivElement, SelectButtonProps>(
  (
    {
      selectedOption,
      handleOpen,
      handleReset,
      placeholder,
      animatedPlaceholder,
      selected,
      error,
      hideErrorMessage,
      disabled,
      defaultValue,
      canReset,
      withBorderMode,
      selectedOptionWithoutIcon,
      active,
      noSelectioinIfOneOption,
      ...scProps
    },
    ref
  ) => {
    const { arrowSelectIcon: ArrowSelectIcon } = useTheme();
    return (
      <ScSelectButton
        ref={ref}
        {...scProps}
        active={!!active}
        canReset={canReset}
        disabled={disabled}
        error={error != null && error !== ''}
        hasArrow={!noSelectioinIfOneOption}
        onClick={handleOpen}
        selected={!!selected}
      >
        <div className="border" />
        <div className="content">
          <div
            className={`value ${
              selectedOption?.icon != null && !selectedOptionWithoutIcon
                ? 'with-icon'
                : ''
            } ${
              animatedPlaceholder != null &&
              (selectedOption != null || defaultValue != null)
                ? 'filled'
                : ''
            }`}
          >
            {selectedOption?.label &&
            React.isValidElement(selectedOption.label) ? (
              <div className="custom-label">{selectedOption.label}</div>
            ) : (
              <>
                {!selectedOptionWithoutIcon && selectedOption?.icon != null && (
                  <ImageComponent
                    alt=""
                    height={20}
                    src={selectedOption.icon}
                    width={20}
                  />
                )}
                {(selectedOption || defaultValue) && (
                  <div className="name">
                    {selectedOption == null
                      ? defaultValue
                      : (selectedOption.nameV ?? selectedOption.name)}
                  </div>
                )}
                {!(selectedOption || defaultValue) && placeholder && (
                  <span className="placeholder">{capitalize(placeholder)}</span>
                )}
              </>
            )}
          </div>
          {animatedPlaceholder && (
            <ScAnimatedPlaceholder
              active={!!active}
              className={
                selectedOption != null || defaultValue != null ? 'filled' : ''
              }
            >
              {capitalize(animatedPlaceholder)}
            </ScAnimatedPlaceholder>
          )}
          {!noSelectioinIfOneOption && (
            <div className="arrow">
              {selected && canReset ? (
                <ButtonCross onClick={handleReset} />
              ) : (
                <>
                  <div className="bg-layer" />
                  <ArrowSelectIcon />
                </>
              )}
            </div>
          )}
        </div>
      </ScSelectButton>
    );
  }
);

interface OptionsBlockProps extends ScOptionsProps {
  options: SelectOption[];
  selectedOption?: SelectOption;
  onChange: (value: SelectOption['value']) => void;
  search?: boolean;
  addons?: JSX.Element | null;
  hideEmptyText?: boolean;
  minOptionHeight?: number;
}

const MIN_OPTIONS_LIST_HEIGHT = 208;
const MIN_OPTION_HEIGHT = 32;
const OPTIONS_LIST_GAPS = 8;

export const OptionsBlock = forwardRef<HTMLDivElement, OptionsBlockProps>(
  (
    {
      options,
      selectedOption,
      onChange,
      search,
      maxHeight,
      addons,
      hideEmptyText,
      minOptionHeight = MIN_OPTION_HEIGHT,
      ...props
    },
    forwardedRef
  ) => {
    const [searchValue, setSearchValue] = useState('');

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (ref.current == null || search == null) return;

      ref.current.focus();
    }, [search]);

    const handleChangeSearchValue = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
      },
      [setSearchValue]
    );

    const filteredOptions = useMemo(() => {
      if (searchValue === '' || search == null) return options;

      return options.filter(({ nameS }) => {
        if (nameS == null) return true;
        return nameS.toLowerCase().includes(searchValue.toLowerCase());
      });
    }, [searchValue, options, search]);

    return (
      <ScOptions {...props} ref={forwardedRef}>
        {props.withBorderMode && <div className="border" />}

        <div className="content">
          {search && (
            <ScSelectSearch>
              <input
                onChange={handleChangeSearchValue}
                placeholder="Search..."
                ref={ref}
                type="text"
              />
              <div className="divider" />
            </ScSelectSearch>
          )}
          {filteredOptions.length === 0 ? (
            hideEmptyText ? null : (
              <ul className="options-list">
                <li className="not-found">Not found</li>
              </ul>
            )
          ) : (
            <ScrollBlock
              style={{
                height:
                  typeof maxHeight === 'number'
                    ? `${maxHeight}px`
                    : filteredOptions.length >= 6
                      ? `${MIN_OPTIONS_LIST_HEIGHT}px`
                      : `${
                          filteredOptions.length * minOptionHeight +
                          OPTIONS_LIST_GAPS
                        }px`,
                width: '100%',
              }}
            >
              <ul
                className={`options-list ${
                  filteredOptions.length * minOptionHeight <
                  (Number(maxHeight) || MIN_OPTIONS_LIST_HEIGHT)
                    ? 'no-scroll'
                    : ''
                }`}
              >
                {filteredOptions.map((option, index) => (
                  <li
                    className={`${
                      selectedOption?.value === option.value ? 'active' : ''
                    } ${option.icon != null ? 'with-icon' : ''}`}
                    key={option.value}
                    onClick={() => onChange(option.value)}
                  >
                    {React.isValidElement(option.label) ? (
                      option.label
                    ) : (
                      <>
                        {option.icon && (
                          <ImageComponent
                            alt={`${option.name}`}
                            height={20}
                            src={option.icon}
                            width={20}
                          />
                        )}
                        <div className="name">{option.name}</div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </ScrollBlock>
          )}
          {addons && <div className="addons">{addons}</div>}
        </div>
      </ScOptions>
    );
  }
);
